import * as React from 'react'

import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'

import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import BalanceIcon from '@mui/icons-material/Balance';
import Tooltip from '@mui/material/Tooltip';

import { CMenuList } from '../common/MenuList';

import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../slices/auth';

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
}

function formatDate() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

function avatarColor(name) {
  const colors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function Toolbar() {
  const user = useSelector(state => state.auth.user);
  const { first_name, last_name, username, role } = user || {};
  const fullName = [first_name, last_name].filter(Boolean).join(' ') || username || 'User';
  const initials = fullName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  return (
    <Box
      sx={{
        height: 64,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: '28px',
        background: '#fff',
        borderBottom: '1px solid #e8edf5',
        boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}
    >
      {/* Left — Greeting */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
            {getGreeting()},
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#3b82f6' }}>
            {first_name || username || 'there'} 👋
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: '1px' }}>
          {formatDate()}
        </Typography>
      </Box>

      {/* Right — Actions + User */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

        {/* Financial Comparison */}
        <Tooltip title="Financial Comparison" arrow>
          <IconButton
            onClick={() => navigate('/financial-comparison')}
            sx={{
              color: '#64748b',
              borderRadius: '10px',
              '&:hover': { background: '#f1f5f9', color: '#3b82f6' },
            }}
          >
            <BalanceIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </Tooltip>

        {/* Notifications */}
        <Tooltip title="Notifications" arrow>
          <IconButton
            sx={{
              color: '#64748b',
              borderRadius: '10px',
              '&:hover': { background: '#f1f5f9', color: '#3b82f6' },
            }}
          >
            <Badge
              variant="dot"
              sx={{
                '& .MuiBadge-dot': {
                  background: '#ef4444',
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  border: '1.5px solid #fff',
                  top: 2,
                  right: 2,
                },
              }}
            >
              <NotificationsNoneOutlinedIcon sx={{ fontSize: '20px' }} />
            </Badge>
          </IconButton>
        </Tooltip>

        <Box sx={{ width: '1px', height: 28, background: '#e2e8f0', mx: '4px' }} />

        {/* User Profile */}
        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            px: '10px',
            py: '6px',
            borderRadius: '12px',
            cursor: 'pointer',
            border: '1px solid transparent',
            transition: 'all 0.15s ease',
            '&:hover': {
              background: '#f8faff',
              border: '1px solid #dbeafe',
            },
          }}
        >
          <Avatar
            sx={{
              width: 34, height: 34,
              fontSize: '0.8rem', fontWeight: 700,
              background: avatarColor(fullName),
              boxShadow: '0 2px 8px rgba(59,130,246,0.25)',
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>
              {fullName}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', lineHeight: 1.2 }}>
              {role}
            </Typography>
          </Box>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: '18px',
              color: '#94a3b8',
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
          />
        </Box>
      </Box>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: '8px',
            minWidth: 180,
            borderRadius: '12px',
            border: '1px solid #e8edf5',
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            overflow: 'visible',
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: -6,
              right: 20,
              width: 12, height: 12,
              background: '#fff',
              border: '1px solid #e8edf5',
              borderBottom: 'none',
              borderRight: 'none',
              transform: 'rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
      >
        <CMenuList dense>
          <MenuItem sx={{ borderRadius: '8px', mx: '4px', gap: '10px' }}>
            <ListItemIcon sx={{ minWidth: 'auto' }}>
              <PersonOutlineIcon fontSize="small" sx={{ color: '#64748b' }} />
            </ListItemIcon>
            <Typography sx={{ fontSize: '0.875rem', color: '#334155' }}>My Account</Typography>
          </MenuItem>
          <MenuItem sx={{ borderRadius: '8px', mx: '4px', gap: '10px' }}>
            <ListItemIcon sx={{ minWidth: 'auto' }}>
              <SettingsOutlinedIcon fontSize="small" sx={{ color: '#64748b' }} />
            </ListItemIcon>
            <Typography sx={{ fontSize: '0.875rem', color: '#334155' }}>Settings</Typography>
          </MenuItem>
          <Divider sx={{ my: '4px', borderColor: '#f1f5f9' }} />
          <MenuItem onClick={handleLogout} sx={{ borderRadius: '8px', mx: '4px', gap: '10px' }}>
            <ListItemIcon sx={{ minWidth: 'auto' }}>
              <LogoutOutlinedIcon fontSize="small" sx={{ color: '#ef4444' }} />
            </ListItemIcon>
            <Typography sx={{ fontSize: '0.875rem', color: '#ef4444' }}>Logout</Typography>
          </MenuItem>
        </CMenuList>
      </Menu>
    </Box>
  );
}

export default Toolbar;
